import React, { useEffect } from "react";
import { Result } from "antd";
import { CheckCircleOutlined, MailOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import styled from "styled-components";
import SEO from "../components/SEO";
import Button from "../components/Button";
import { APP_NAME, APP_SUPPORT_EMAIL } from "../helpers/constants";

const PurchaseSuccess = () => {
  // Fire celebratory confetti on mount
  useEffect(() => {
    let isCancelled = false;
    let confettiInterval = null;

    const launch = async () => {
      try {
        const confetti = (await import("canvas-confetti")).default;
        if (isCancelled) return;

        // Clumse brand colors
        const defaults = {
          disableForReducedMotion: true,
          colors: [
            "#2C5F2D",
            "#4A7C59",
            "#86E49D",
            "#A4F1B1",
            "#5DD77A",
            "#70F2BA",
            "#F6B438",
          ],
          scalar: 1.2,
          ticks: 250,
          zIndex: 1000,
        };

        const burst = (particleRatio, opts) => {
          confetti({
            ...defaults,
            ...opts,
            particleCount: Math.floor(250 * particleRatio),
          });
        };

        // Immediate initial bursts - start fast!
        // Left side burst
        burst(0.3, {
          spread: 26,
          startVelocity: 40,
          angle: 75,
          origin: { y: 0.1, x: 0 },
        });
        // Right side burst
        burst(0.3, {
          spread: 26,
          startVelocity: 40,
          angle: 105,
          origin: { y: 0.1, x: 1 },
        });
        // Left side burst
        burst(0.25, { spread: 60, angle: 75, origin: { y: 0.1, x: 0 } });
        // Right side burst
        burst(0.25, { spread: 60, angle: 105, origin: { y: 0.1, x: 1 } });
        // Wide center burst
        burst(0.4, { spread: 100, decay: 0.95, origin: { y: 0.1, x: 0.5 } });

        // Quick follow-up bursts
        setTimeout(() => {
          if (!isCancelled) {
            burst(0.3, {
              spread: 70,
              scalar: 1.0,
              angle: 75,
              origin: { y: 0.12, x: 0 },
            });
            burst(0.3, {
              spread: 70,
              scalar: 1.0,
              angle: 105,
              origin: { y: 0.12, x: 1 },
            });
          }
        }, 200);

        // Extended celebration - continuous bursts for longer duration
        const duration = 1000; // 1 second of continuous confetti
        const end = Date.now() + duration;

        confettiInterval = setInterval(() => {
          if (Date.now() > end || isCancelled) {
            clearInterval(confettiInterval);
            return;
          }

          // Continuous burst from center
          confetti({
            ...defaults,
            particleCount: 20,
            angle: 90,
            spread: 80,
            origin: { x: 0.5, y: 0.1 },
            colors: defaults.colors,
          });
        }, 150); // Burst every 150ms for smooth continuous effect

        // Final big burst at the end
        setTimeout(() => {
          if (!isCancelled) {
            burst(0.8, {
              spread: 90,
              scalar: 1.1,
              origin: { y: 0.15, x: 0.5 },
            });
          }
        }, duration);
      } catch (e) {
        // If the dependency isn't available, silently skip
        console.warn("Confetti not available:", e);
      }
    };

    launch();
    return () => {
      isCancelled = true;
      if (confettiInterval) {
        clearInterval(confettiInterval);
      }
    };
  }, []);

  const seoTitle = "Welcome to Clumse: Account Being Provisioned";

  const welcomeTitle = `Welcome to ${APP_NAME}!`;

  return (
    <>
      <SEO
        title={seoTitle}
        description="Your account is being set up. Check your email for login credentials."
      />
      <SuccessBg>
        <SuccessContainer>
          <Result
            icon={
              <CheckCircleOutlined style={{ color: "#2C5F2D", fontSize: 48 }} />
            }
            status="success"
            title={<SuccessTitle>{welcomeTitle}</SuccessTitle>}
            subTitle={
              <SuccessMessage>
                <p>
                  Your account is being provisioned now. Login credentials will
                  arrive in your inbox within the next 2 to 3 minutes. If you
                  don&rsquo;t see the email, check your spam folder.
                </p>
                <EmailStepBox>
                  <MailOutlined
                    style={{ fontSize: 28, color: "#2c5f2d", marginBottom: 10 }}
                  />
                  <strong>Next step: check your email</strong>
                  <span>
                    Your welcome email includes your temporary password and
                    setup instructions. You&rsquo;ll be up and running in
                    minutes.
                  </span>
                </EmailStepBox>
                <InspireBox>
                  <strong>Get started in a few minutes:</strong>
                  <ol>
                    <li>
                      <b>Confirm your doors</b> under Doors so vendor outreach
                      matches the right asset.
                    </li>
                    <li>
                      <b>Open a repair</b> with photos and notes so Clumse can
                      invite vetted pros.
                    </li>
                    <li>
                      <b>Compare bids</b> in Vendor bidding and pick the team
                      you trust.
                    </li>
                  </ol>
                  <span className="brand-primary">
                    Bid-first workflows, fees tied to savings you approve.
                  </span>
                </InspireBox>
                <p>
                  Questions? Contact us at{" "}
                  <a href={`mailto:${APP_SUPPORT_EMAIL}`}>
                    {APP_SUPPORT_EMAIL}
                  </a>
                  .
                </p>
              </SuccessMessage>
            }
            extra={[
              <LoginButton key="login" variant="primary">
                <Link to="/login">Go to Login</Link>
              </LoginButton>,
              <SecondaryButtonWrap key="home">
                <Link to="/">Return to Home</Link>
              </SecondaryButtonWrap>,
            ]}
          />
        </SuccessContainer>
      </SuccessBg>
    </>
  );
};

const SuccessBg = styled.div`
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(
    120deg,
    #181818 0%,
    #2c5f2d 40%,
    #4a7c59 75%,
    #e6f3e6 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SuccessContainer = styled.div`
  max-width: 800px;
  margin: 80px auto;
  padding: 64px;
  background: var(--color-bg-secondary, #fff);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(44, 95, 45, 0.18),
    0 2px 24px rgba(24, 24, 24, 0.1);
  border: 2.5px solid #2c5f2d;
  text-align: center;
  @media (max-width: 767px) {
    max-width: 98vw;
    padding: 32px 8px;
  }
`;

const SuccessTitle = styled.div`
  font-family: "Inter", system-ui, ui-sans-serif, sans-serif;
  font-size: 2.2rem;
  font-weight: 700;
  color: #2c5f2d;
  margin-bottom: 0.5rem;
  letter-spacing: -0.02em;
`;

const SuccessMessage = styled.div`
  text-align: center;
  margin: 32px 0;
  font-family: "Inter", system-ui, ui-sans-serif, sans-serif;
  color: #333;
  font-size: 1.1rem;
  p {
    margin-bottom: 20px;
    color: #555;
    font-size: 1.1rem;
    line-height: 1.6;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
  a {
    color: #2c5f2d;
    text-decoration: underline;
    font-weight: 600;
  }
  .brand-primary {
    color: #2c5f2d;
    font-weight: 700;
  }
`;

const InspireBox = styled.div`
  margin: 2.5rem auto 2rem auto;
  max-width: 600px;
  background: linear-gradient(90deg, #f0f8f0 0%, #e8f5e8 100%);
  border: 1.5px solid #2c5f2d;
  border-radius: 14px;
  padding: 2rem 1.5rem;
  color: #4a7c59;
  font-family: "Inter", system-ui, ui-sans-serif, sans-serif;
  font-size: 1rem;
  text-align: left;
  box-shadow: 0 2px 12px rgba(44, 95, 45, 0.08);
  ul,
  ol {
    margin: 1rem 0 1rem 1.5rem;
    padding: 0;
  }
  ul {
    list-style: disc;
  }
  ol {
    list-style: decimal;
  }
  li {
    margin-bottom: 0.65rem;
    font-size: 1rem;
    line-height: 1.55;
  }
  strong {
    color: #2c5f2d;
    font-weight: 700;
    font-size: 1.1rem;
    display: block;
    margin-bottom: 0.5rem;
  }
  .brand-primary {
    display: block;
    margin-top: 1.25rem;
    color: #2c5f2d;
    font-weight: 700;
    font-size: 1.1rem;
    text-align: center;
  }
`;

const LoginButton = styled(Button)`
  background: linear-gradient(90deg, #2c5f2d 0%, #4a7c59 100%) !important;
  color: #fff !important;
  border: none !important;
  border-radius: 12px !important;
  font-family: "Inter", system-ui, ui-sans-serif, sans-serif !important;
  font-weight: 700 !important;
  font-size: 1.08rem !important;
  padding: 0.9rem 2.2rem !important;
  box-shadow: 0 4px 16px rgba(44, 95, 45, 0.18) !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
  &:hover {
    background: linear-gradient(90deg, #4a7c59 0%, #2c5f2d 100%) !important;
    color: #fff !important;
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 8px 32px rgba(44, 95, 45, 0.25) !important;
  }

  a {
    color: #fff !important;
    text-decoration: none;
  }
`;

const SecondaryButtonWrap = styled.div`
  margin-top: 0.5rem;
  a {
    color: #4a7c59;
    font-weight: 600;
    font-size: 0.95rem;
    text-decoration: underline;
    font-family: "Inter", -apple-system, sans-serif;
  }
`;

const EmailStepBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  background: #f0fdf4;
  border: 1.5px solid #2c5f2d;
  border-radius: 12px;
  padding: 1.5rem;
  margin: 0 auto 1.5rem;
  max-width: 500px;

  strong {
    color: #2c5f2d;
    font-size: 1.05rem;
    font-weight: 700;
    font-family: "Inter", -apple-system, sans-serif;
  }

  span {
    color: #4b5563;
    font-size: 0.95rem;
    line-height: 1.5;
    text-align: center;
  }
`;

export default PurchaseSuccess;
